import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/helper/api";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FormProvider, useForm } from "react-hook-form";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import styles from "@/components/forms/userSettingsForm.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import GenericControllerWithInput from "@/components/forms/inputsWithController/GenericController";
import DropDown from "@/components/forms/inputs/dropDown";
import i18n from "@/translations/i18n";
import { useDispatch } from "react-redux";
import { setPreferredLanguage } from "@/components/state/parent/loginSlice";
import { useConfirmationModal } from "@/context/confirmationModalContext";

interface Props {
  userInfos?: any;
  onDataChange: () => void;
}

const LanguageSettingsForm: FC<Props> = ({ userInfos, onDataChange }) => {
  const { handleError, errorModal } = useErrorModal();
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();

  const { showConfirmationModalAsync } = useConfirmationModal();

  const methods = useForm<any>({
    mode: "onChange",
  });

  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    methods.setValue(
      "preferred_language",
      userInfos?.person?.preferred_language,
    );
  }, [userInfos]);

  const onSubmit = async (data: any) => {
    const confirmLanguageChange = await showConfirmationModalAsync(
      t("confirm_language_change_reload"),
    );
    if (!confirmLanguageChange) return;

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    api.genericApiRequest({
      entity: "user",
      method: "put",
      entityId: userInfos.user_id,
      successHandler: () => {
        setNotificationMessage(t("save_successful"));
        setNotificationVariant("success");
        setSuccessForNotification(true);

        const newLang = data.preferred_language.value;
        const currentLang = userInfos?.person?.preferred_language;

        if (currentLang !== newLang) {
          i18n.changeLanguage(newLang);
          dispatch(setPreferredLanguage(newLang));
        }
        onDataChange();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      submitData: { preferred_language: data.preferred_language.value },
      config: config,
    });
  };

  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };

  const languageOptions = [
    {
      value: "de",
      label: t("language_german"),
    },
    {
      value: "en",
      label: t("language_english"),
    },
  ];

  return (
    <>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t("language_settings")}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              className={styles.form}
              noValidate
            >
              <GenericControllerWithInput
                control={methods.control}
                name={"preferred_language"}
                renderInput={({ onChange, value }) => (
                  <DropDown
                    options={languageOptions}
                    onChange={onChange}
                    name="preferred_language"
                    label={t("preferred_language")}
                    defaultValue={value}
                  />
                )}
              />

              <GenericButton className={styles.submitButton} type="submit">
                {t("save")}
              </GenericButton>
            </form>
          </FormProvider>
        </AccordionDetails>
      </Accordion>
      {errorModal}
      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default LanguageSettingsForm;
